import { z } from 'zod'

const _localDatetime = z.iso.datetime({ local: true })

/***************** 1:1 문의 *****************/
export const INQUIRY_TYPE = ['FUNCTION_INQUIRY', 'FUNCTION_BUG', 'CONTENT', 'FAQ', 'ETC'] as const
export const InquiryTypeSchema = z.enum(INQUIRY_TYPE)
export type InquiryType = z.infer<typeof InquiryTypeSchema>

export const InquiryAttachmentSchema = z.object({
  /** 인코딩 없이 그대로 열 수 있는 CloudFront 주소 */
  fileUrl: z.string(),
  fileName: z.string(),
})
export type InquiryAttachment = z.infer<typeof InquiryAttachmentSchema>

// 응답 키(question*·replyDate)는 서버가 레거시 호환을 위해 붙인 이름 그대로다.
export const InquirySchema = z.object({
  id: z.number(),
  questionType: InquiryTypeSchema,
  questionTitle: z.string(),
  questionContent: z.string(),
  questionDate: _localDatetime,
  solved: z.boolean(),
  replyContent: z.string().nullable(),
  replyDate: _localDatetime.nullable(),
  attachments: z.array(InquiryAttachmentSchema).nullable(),
})
export type Inquiry = z.infer<typeof InquirySchema>

export const InquiryAttachmentPresignedUrlSchema = z.object({
  presignedUrl: z.string(),
  s3Key: z.string(),
  fileName: z.string(),
  /** 업로드할 때 Content-Type 헤더로 이 값을 그대로 보내야 한다. 다르면 S3 가 서명 검증에서 거부한다 */
  contentType: z.string(),
})
export type InquiryAttachmentPresignedUrl = z.infer<typeof InquiryAttachmentPresignedUrlSchema>

/***************** 콘텐츠 오류 신고 *****************/
// 신고 대상(문제·교재 문제·개념·소단원·학습지)마다 서버가 받는 분류가 다르다. 문제 계열은 네 분류를 다 받고 나머지는
// ERROR_CATEGORY 만 받는다. 세부 분류는 세 서버 enum(ProblemBugReportSubtype·ConceptBugReportSubtype·
// WorksheetBugReportSubtype)의 합이고, 서버는 세부 분류가 고른 분류에 속하지 않으면 거부한다.
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 bugReport/controller/BugReportController.kt·model/type/ 로 확인.
/** 신고 대상. PUBLIC_PROBLEM 은 이름과 달리 모든 교재 문제를 가리킨다 */
export const CONTENT_BUG_REPORT_CATEGORY = [
  'PROBLEM',
  'PUBLIC_PROBLEM',
  'CONCEPT',
  'LITTLE_CHAPTER',
  'WORKSHEET',
] as const

export const CONTENT_BUG_REPORT_TYPE = [
  'ERROR_CATEGORY',
  'CONCEPT_DIVISION',
  'LEVEL_SUGGESTION',
  'DUPLICATE_PROBLEM',
] as const

export const CONTENT_BUG_REPORT_SUBTYPE = [
  'ERROR_CATEGORY_PROBLEM',
  'ERROR_CATEGORY_ANSWER',
  'ERROR_CATEGORY_SOLUTION',
  'ERROR_CATEGORY_LAYOUT',
  'ERROR_CATEGORY_TYPO',
  'ERROR_CATEGORY_ETC',
  'ERROR_CATEGORY_NEED_ANSWER',
  'ERROR_CATEGORY_MATCH',
  'ERROR_CATEGORY_LACK_CONTENT',
  'ERROR_CATEGORY_CONTENT',
  'ERROR_CATEGORY_NONE_CONTENT',
  'ERROR_CATEGORY_NEED_SHORTEN_CONTENT',
  'ERROR_CATEGORY_NONE_LAYOUT',
  'CONCEPT_DIVISION',
  'CONCEPT_DIVISION_PROBLEM',
  'LEVEL_SUGGESTION_LOWEST',
  'LEVEL_SUGGESTION_LOW',
  'LEVEL_SUGGESTION_MIDDLE',
  'LEVEL_SUGGESTION_HIGH',
  'LEVEL_SUGGESTION_HIGHEST',
  'DUPLICATE_PROBLEM',
] as const

export const ContentBugReportCategorySchema = z.enum(CONTENT_BUG_REPORT_CATEGORY)
export type ContentBugReportCategory = z.infer<typeof ContentBugReportCategorySchema>

export const ContentBugReportTypeSchema = z.enum(CONTENT_BUG_REPORT_TYPE)
export type ContentBugReportType = z.infer<typeof ContentBugReportTypeSchema>

export const ContentBugReportSubtypeSchema = z.enum(CONTENT_BUG_REPORT_SUBTYPE)
export type ContentBugReportSubtype = z.infer<typeof ContentBugReportSubtypeSchema>

/***************** 정식가입 상담 신청 *****************/
// 무료체험 학원이 정식가입 상담을 신청한 화면. 홈페이지 서버가 받는다.
export const CONTRACT_CONTACT_INCOMING_PATH = [
  'SIGNATURE_WORKBOOK',
  'CUSTOM_WORKBOOK',
  'CART',
  'PRINT_DOCUMENT',
  'MY_DB_UPLOAD',
  'MY_DB_DOWNLOAD',
] as const
export const ContractContactIncomingPathSchema = z.enum(CONTRACT_CONTACT_INCOMING_PATH)
export type ContractContactIncomingPath = z.infer<typeof ContractContactIncomingPathSchema>
